// @flow
import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { compose } from 'redux';
import classnames from 'classnames';

import CapacitorRipple from '../capacitor/Ripple';
import * as playableItemPreviewActions from '../../actions/playableItemPreview';
import collectibleEntity from '../../hoc/collectibleEntity';
import {
  selectPlayableItemPreviewIsLoadingToPlay,
  selectPlayableItemPreviewIsLoading,
  selectPlayableItemPreviewIsPlaying,
} from '../../selectors/playableItemPreview';

import Image from '../util/Image';
import { CircularPlayButton } from './CircularPlayButton';
import Curator from './Curator';

import styles from './PlayableItemPreview.css';

import { getQueueOrigin, getQueueOriginUrl } from '../../lib/queue';
import {
  ENTITY_TYPE_PLAYLIST_GROUP,
  ENTITY_TYPE_PLAYLIST,
  ENTITY_TYPE_ALBUM,
  ENTITY_TYPE_RECORDING,
} from '../../constants';
import { PLAYABLE_ITEM_PREVIEW_DIMS } from '../../lib/imgix';
import { commonMessages } from '../messages/Common';
import CollectionButton from './CollectionButton';
import { loadEntity } from '../../lib/entities';

const preventDefault = e => e.preventDefault();

declare var __CAPACITOR__: boolean;

type OwnProps = {
  item: Object,
  beforeTogglePlay?: Function,
  onClick?: Function,
  showPlayButton: boolean,
  groupId?: string,
  isInCollection: boolean,
  toggleIsInCollection: Function,
  children: React$Element<any>,
  showCollectionButtonOnHover?: boolean,
};

type MapStateToProps = {
  isPlaying: boolean,
  isLoading: boolean,
};

type DispatchProps = {
  togglePlay: Function,
  loadPlayableItemPreviewUrl: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps & { intl: IntlShape };

const PlayableItemPreview = ({
  item,
  isPlaying,
  isLoading,
  beforeTogglePlay,
  togglePlay,
  onClick,
  loadPlayableItemPreviewUrl,
  intl,
  showPlayButton = true,
  groupId,
  isInCollection,
  toggleIsInCollection,
  children,
  showCollectionButtonOnHover,
}: Props) => {
  const { content, imageUrl, title, subtitle } = item;
  const { composerName, curator, type } = content;

  const onClickItem = () => {
    if (onClick) {
      onClick(item, groupId);
    }
  };

  const onMouseOver = () => {
    loadPlayableItemPreviewUrl(item);
  };

  const onTogglePlay = playing => {
    if (beforeTogglePlay) {
      beforeTogglePlay(item, playing, groupId);
    }

    return togglePlay(item, Date.now());
  };

  const showCollectionButton =
    type === ENTITY_TYPE_PLAYLIST || type === ENTITY_TYPE_ALBUM || type === ENTITY_TYPE_RECORDING;

  const toggleInCollection = async event => {
    event.preventDefault();
    await loadEntity(type, content);
    toggleIsInCollection();
  };

  const imageWidth = PLAYABLE_ITEM_PREVIEW_DIMS.w;
  const imageHeight = PLAYABLE_ITEM_PREVIEW_DIMS.h;
  const imageStyle = styles.bg;
  const isPlaylistGroup = type === ENTITY_TYPE_PLAYLIST_GROUP;
  const shouldShowPlayButton = !isPlaylistGroup && showPlayButton;

  let url;

  if (isPlaylistGroup) {
    url = `/discover/playlist-group/${content.slug}`;
  } else {
    const queueOrigin = getQueueOrigin(type, content);
    url = getQueueOriginUrl(queueOrigin);
  }

  const ariaLink = intl.formatMessage(commonMessages.view, { title });

  return (
    <div className={styles.component}>
      <div className={styles.cover}>
        <Image
          src={imageUrl}
          width={imageWidth}
          height={imageHeight}
          className={imageStyle}
          alt=""
        />
        {shouldShowPlayButton && (
          <div className={styles.play}>
            <CircularPlayButton
              onClick={onTogglePlay}
              playing={isPlaying}
              loading={isLoading}
              light
              className={styles.playBtn}
              data-test="playable-item-preview.play-btn"
            />
          </div>
        )}
      </div>
      {composerName && <p className={`${styles.composerName} fz--delta`}>{composerName}</p>}
      <div className={styles.titleContainer}>
        <span className={styles.title}>{title}</span>
        {showCollectionButton && (
          <CollectionButton
            active={isInCollection}
            onClick={toggleInCollection}
            className={classnames(styles.collectionButton, {
              [styles.collectionButtonOnHover]: showCollectionButtonOnHover,
            })}
          />
        )}
      </div>
      {subtitle && <span className={styles.performance}>{subtitle}</span>}
      {children}
      {curator && (
        <div className={styles.curator}>
          <Curator curator={curator} />
        </div>
      )}
      <Link
        to={url}
        className={classnames('c-link--expand-hitbox', styles.link)}
        onClick={onClickItem}
        onMouseOver={shouldShowPlayButton ? onMouseOver : null}
        aria-label={ariaLink}
        // Disable HTML5 drag in the slider
        draggable={false}
        onDragStart={preventDefault}
        data-test="playable-item-preview.link"
      >
        <CapacitorRipple />
      </Link>
    </div>
  );
};

function makeMapStateToProps() {
  let prevItem = null;
  let prevNormalizedItem;

  return (state: Object, ownProps: OwnProps): MapStateToProps => {
    const { item } = ownProps;

    if (item !== prevItem) {
      prevItem = item;
      if (item.content.type === ENTITY_TYPE_PLAYLIST || item.content.type === ENTITY_TYPE_ALBUM) {
        prevNormalizedItem = {
          type: item.content.type,
          id: item.content.id,
          slug: item.content.slug,
          title: item.title,
          imageUrl: item.imageUrl,
        };
      } else {
        prevNormalizedItem = undefined;
      }
    }

    return {
      isPlaying: selectPlayableItemPreviewIsPlaying(state, item),
      isLoading: __CAPACITOR__
        ? selectPlayableItemPreviewIsLoading(state, item)
        : selectPlayableItemPreviewIsLoadingToPlay(state, item),
      album: prevNormalizedItem,
      playlist: prevNormalizedItem,
    };
  };
}

const dispatchProps: DispatchProps = {
  togglePlay: playableItemPreviewActions.togglePlayPlayableItemPreview,
  loadPlayableItemPreviewUrl: playableItemPreviewActions.loadPlayableItemPreviewUrl,
};

function getCollectibleEntityDescription(props) {
  return {
    id: props.item.content.id,
    trackingSource: props.item.content.type === ENTITY_TYPE_PLAYLIST ? 'Playlist' : 'Album',
  };
}

export default compose(
  connect(makeMapStateToProps, dispatchProps),
  injectIntl,
  collectibleEntity(props => props.item.content.type, getCollectibleEntityDescription)
)(PlayableItemPreview);
